import { AdminRole } from "@repo/api-dtos"
import { isOpenApiAccessRequestStatus } from "@repo/shared-primitives"
import { createFileRoute, Link } from "@tanstack/react-router"
import { format } from "date-fns"
import { ArrowLeft, FileText, Gavel, MessagesSquare } from "lucide-react"
import {
	DetailPanel,
	DetailPanelSection,
	PageHeader,
} from "@/components/layout"
import { Skeleton } from "@/components/ui/skeleton"
import { ApiAccessRequestAnswers } from "@/features/api-requests-admin/ApiAccessRequestAnswers"
import {
	ApplicantApiActivityCard,
	ApplicantCard,
	ApplicantOtherRequestsCard,
} from "@/features/api-requests-admin/ApiAccessRequestApplicant"
import { ApiAccessRequestConversation } from "@/features/api-requests-admin/ApiAccessRequestConversation"
import {
	ApiAccessRequestDecisionForm,
	ApiAccessRequestDecisionSummary,
} from "@/features/api-requests-admin/ApiAccessRequestDecision"
import { ApiAccessRequestDiscordCard } from "@/features/api-requests-admin/ApiAccessRequestDiscordCard"
import { ApiAccessRequestStatusBadge } from "@/features/api-requests-admin/ApiAccessRequestStatusBadge"
import { ACCESS_KIND_LABELS } from "@/features/api-requests-admin/api-access-request-labels"
import { useAdminRoles } from "@/hooks/use-admin-roles"
import { useApiAccessRequest } from "@/queries/marketplace"

export const Route = createFileRoute("/api-requests/$requestId")({
	component: ApiAccessRequestPage,
})

function BackLink() {
	return (
		<Link
			to="/api-requests"
			search={{} as never}
			className="inline-flex w-fit items-center gap-1 text-meta text-xs hover:text-foreground"
		>
			<ArrowLeft className="h-3.5 w-3.5" />
			Access requests
		</Link>
	)
}

/**
 * One API access request: Answers, Conversation and Decision panels on the
 * left, the applicant's summary panels on the right (stacked below lg).
 */
function ApiAccessRequestPage() {
	const { requestId } = Route.useParams()
	const { data: request, isLoading, error } = useApiAccessRequest(requestId)
	const { hasRole } = useAdminRoles()
	const canViewApiActivity = hasRole(AdminRole.IntegrationsAdmin)

	if (isLoading) {
		return (
			<div className="flex flex-col gap-4 p-4 sm:p-6">
				<Skeleton className="h-8 w-72 max-w-full" />
				<Skeleton className="h-64 w-full" />
			</div>
		)
	}

	if (error || !request) {
		return (
			<div className="flex flex-col gap-4 p-4 sm:p-6">
				<BackLink />
				<div className="rounded-lg bg-bad-soft px-4 py-3 text-bad">
					{error?.message ?? "API access request not found"}
				</div>
			</div>
		)
	}

	return (
		<div key={request.id} className="flex flex-col gap-6 p-4 sm:p-6">
			<div className="flex flex-col gap-3">
				<BackLink />
				<PageHeader
					title={
						<span className="inline-flex flex-wrap items-center gap-x-3 gap-y-1">
							API access request from @{request.applicant.username}
							<ApiAccessRequestStatusBadge
								request={request}
								className="font-normal text-sm"
							/>
						</span>
					}
					description={
						<>
							Submitted on{" "}
							<span className="tabular-nums">
								{format(new Date(request.createdAt), "MMM d, yyyy 'at' h:mm a")}
							</span>{" "}
							· requested{" "}
							{ACCESS_KIND_LABELS[request.requestedAccess].toLowerCase()}
						</>
					}
				/>
			</div>

			<div className="grid items-start gap-6 lg:grid-cols-[minmax(0,1fr)_340px] lg:gap-8">
				<div className="flex min-w-0 flex-col gap-4">
					<DetailPanel title="Answers" icon={FileText}>
						<DetailPanelSection>
							<ApiAccessRequestAnswers request={request} />
						</DetailPanelSection>
					</DetailPanel>

					<DetailPanel
						title="Conversation"
						icon={MessagesSquare}
						badges={
							request.messages.length > 0 && (
								<span className="text-xs text-meta tabular-nums">
									{request.messages.length}
								</span>
							)
						}
					>
						<ApiAccessRequestConversation request={request} />
					</DetailPanel>

					<DetailPanel title="Decision" icon={Gavel}>
						<DetailPanelSection>
							<p className="text-xs text-meta">
								Approving or rejecting emails the applicant. Approving can also
								enable API access on their account (public-api flag) and give
								them the Discord roles with a welcome message. Approving as an
								app can also create the integration app for the applicant,
								disabled until you enable it on its page.
							</p>
							{isOpenApiAccessRequestStatus(request.status) ? (
								<ApiAccessRequestDecisionForm request={request} />
							) : (
								<ApiAccessRequestDecisionSummary request={request} />
							)}
						</DetailPanelSection>
					</DetailPanel>
				</div>

				<div className="flex min-w-0 flex-col gap-4">
					<ApplicantCard request={request} />
					<ApiAccessRequestDiscordCard request={request} />
					{canViewApiActivity && <ApplicantApiActivityCard request={request} />}
					<ApplicantOtherRequestsCard request={request} />
				</div>
			</div>
		</div>
	)
}
